// Shared single-select button list for player-side game control screens.

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from '@/utilities/cn';

export type ButtonOption = {
  value: string;
  label: React.ReactNode;
  variant?: React.ComponentProps<typeof Button>['variant'];
  style?: React.CSSProperties;
};

interface PlayingButtonListProps {
  options: ButtonOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  columns?: 1 | 2 | 3;
}

const optionButtonClass =
  'h-auto min-h-11 w-full min-w-0 shrink-0 justify-center whitespace-normal py-3 text-base';

const gridColsClass = (columns: 1 | 2 | 3): string => {
  switch (columns) {
    case 2:
      return 'grid-cols-2';
    case 3:
      return 'grid-cols-3';
    case 1:
    default:
      return 'grid-cols-1';
  }
};

const PlayingButtonList: React.FC<PlayingButtonListProps> = ({
  options,
  selectedValue,
  onSelect,
  columns = 1,
}) => {
  if (options.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-2.5">
        <Empty className="min-h-44 rounded-lg border border-solid bg-card py-8 shadow-sm">
          <EmptyHeader>
            <EmptyTitle>No choices available</EmptyTitle>
            <EmptyDescription>
              There aren&apos;t any options to pick right now. Hang tight.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  // NOTE: native overflow div, NOT Radix ScrollArea.
  // Radix ScrollArea uses a custom pointer-event-driven viewport that confuses
  // vaul's drag-vs-scroll heuristic, so touch scrolling silently fails inside a
  // Drawer (vaul#168, vaul#358, vaul#495, shadcn#2167). The reliable fix is a
  // native scrollable element so the browser's pointercancel hand-off lets vaul
  // yield to native scroll. `data-vaul-no-drag` prevents drag-init on this
  // subtree; `overscroll-contain` stops scroll-chaining into the drawer body.
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      data-vaul-no-drag
    >
      <div
        className={cn(
          'grid w-full gap-2 px-4 pb-4 pt-2.5',
          gridColsClass(columns)
        )}
      >
        {options.map((option) => {
          const selected = selectedValue === option.value;

          return (
            <Button
              key={option.value}
              type="button"
              variant={selected ? 'secondary' : (option.variant ?? 'default')}
              size="xl"
              style={option.style}
              className={cn(optionButtonClass)}
              onClick={() => onSelect(option.value)}
            >
              {option.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default PlayingButtonList;
